import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Loader2, Play, Eye, X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'
import { nextStepPercent } from '@/hooks/useZoom'
import {
  clampPercent,
  snapPercentToStep,
  ZOOM_MIN_PERCENT,
  ZOOM_MAX_PERCENT_RASTER,
} from '@/constants/zoom'
import { ZoomReadout } from '@/components/intueri/ZoomReadout'
import { toast } from 'sonner'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'

export interface AnalyzedLine {
  line_number: string
  source: string
  destination: string
}

interface ConnectionFlow {
  connection_id: string
  coords: Array<[number, number]>
  upstream_point: [number, number] | null
  downstream_point: [number, number] | null
  seed_source: string | null
  is_forked: boolean
  line_numbers: string[]
  note: string | null
}

interface LinesTabProps {
  documentId: string
}

const SEED_COLOR: Record<string, string> = {
  inlet: '#10b981',         // green
  outlet: '#f59e0b',        // amber
  arrow: '#3b82f6',         // blue
  propagated: '#8b5cf6',    // purple
  conservation: '#ec4899',  // pink
}
const UNKNOWN_COLOR = '#9ca3af'  // gray
const FORKED_COLOR = '#ef4444'   // red

export function LinesTab({ documentId }: LinesTabProps) {
  const { t } = useTranslation()
  const [lines, setLines] = useState<AnalyzedLine[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [flowsOpen, setFlowsOpen] = useState(false)

  const handleCalculate = async () => {
    setIsLoading(true)
    try {
      const response = await apiService.analyzeLines(documentId)
      setLines(response.lines)
    } catch (err) {
      console.error('Failed to analyze lines', err)
      toast.error(t('document_viewer:lines.toastAnalyzeFailed', { defaultValue: 'Failed to analyze lines' }))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="h-full overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{t('document_viewer:lines.title', { defaultValue: 'Lines' })}</h2>
          <p className="text-sm text-gray-500 dark:text-dark-text-muted">
            {t('document_viewer:lines.description', { defaultValue: 'Generate line number, source, and destination for each pipe in this P&ID.' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setFlowsOpen(true)}>
            <Eye className="w-4 h-4 mr-2" />
            {t('document_viewer:lines.showFlows', { defaultValue: 'Show flows' })}
          </Button>
          <Button onClick={handleCalculate} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            {isLoading
              ? t('document_viewer:lines.generating', { defaultValue: 'Generating…' })
              : t('document_viewer:lines.generate', { defaultValue: 'Generate lines' })}
          </Button>
        </div>
      </div>

      {lines === null ? (
        <div className="text-sm italic text-center py-12 text-gray-400 dark:text-gray-500">
          {t('document_viewer:lines.emptyNotGenerated', { defaultValue: 'No lines generated yet. Press Generate to analyze this P&ID.' })}
        </div>
      ) : lines.length === 0 ? (
        <div className="text-sm italic text-center py-12 text-gray-400 dark:text-gray-500">
          {t('document_viewer:lines.emptyNoneDetected', { defaultValue: 'No lines detected.' })}
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-dark-muted text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('document_viewer:lines.colLineNumber', { defaultValue: 'Line number' })}</th>
                <th className="px-4 py-2 font-medium">{t('document_viewer:lines.colSource', { defaultValue: 'Source' })}</th>
                <th className="px-4 py-2 font-medium">{t('document_viewer:lines.colDestination', { defaultValue: 'Destination' })}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-4 py-2 font-mono">{line.line_number || '—'}</td>
                  <td className="px-4 py-2">{line.source || '—'}</td>
                  <td className="px-4 py-2">{line.destination || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {flowsOpen && (
        <FlowsOverlayModal documentId={documentId} onClose={() => setFlowsOpen(false)} />
      )}
    </div>
  )
}

interface FlowsOverlayModalProps {
  documentId: string
  onClose: () => void
}

function FlowsOverlayModal({ documentId, onClose }: FlowsOverlayModalProps) {
  const { t } = useTranslation()
  const [flows, setFlows] = useState<ConnectionFlow[] | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'known' | 'unknown'>('all')
  const [scale, setScale] = useState<number | null>(null)   // null => auto-fit on next layout
  const [showLabels, setShowLabels] = useState(true)
  const imgUrlRef = useRef<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [flowsRes, imageBlob] = await Promise.all([
          apiService.analyzeFlows(documentId),
          apiService.downloadPnidImage(documentId).catch(() => null),
        ])
        if (cancelled) return
        setFlows(flowsRes.flows)
        if (imageBlob) {
          const url = URL.createObjectURL(imageBlob)
          imgUrlRef.current = url
          setImageUrl(url)
        }
      } catch (err) {
        console.error('Failed to load flow debug data', err)
        if (!cancelled) setError(t('document_viewer:flows.loadError', { defaultValue: 'Failed to load flow data' }))
      }
    })()
    return () => {
      cancelled = true
      if (imgUrlRef.current) {
        URL.revokeObjectURL(imgUrlRef.current)
        imgUrlRef.current = null
      }
    }
  }, [documentId])

  // Measure the image off-DOM so we know its intrinsic size before we render
  // the sized wrapper (avoids a flash of full-resolution image).
  useEffect(() => {
    if (!imageUrl) return
    const probe = new Image()
    probe.onload = () => setImageSize({ w: probe.naturalWidth, h: probe.naturalHeight })
    probe.src = imageUrl
  }, [imageUrl])

  const fitScale = (): number | null => {
    if (!imageSize || !scrollRef.current) return null
    const c = scrollRef.current
    // Slight padding so edges aren't flush
    const availW = Math.max(1, c.clientWidth - 16)
    const availH = Math.max(1, c.clientHeight - 16)
    return Math.min(availW / imageSize.w, availH / imageSize.h)
  }

  // On first layout after image dimensions are known, auto-fit to viewport.
  useLayoutEffect(() => {
    if (scale == null && imageSize && scrollRef.current) {
      const s = fitScale()
      if (s) setScale(s)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSize])

  // FS-562: grid-snapped 10% steps (was ×1.5 / ÷1.5, which produced off-grid
  // readouts like 67% / 44%). 100% == actual size; clamped to the raster policy.
  const stepZoom = (dir: 1 | -1) =>
    setScale((s) =>
      s ? clampPercent(nextStepPercent(Math.round(s * 100), dir), ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_RASTER) / 100 : s,
    )
  const zoomIn = () => stepZoom(1)
  const zoomOut = () => stepZoom(-1)
  const handleSetZoom = (percent: number) =>
    setScale(clampPercent(snapPercentToStep(percent), ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_RASTER) / 100)
  const zoomFit = () => {
    const s = fitScale()
    if (s) setScale(s)
  }
  const zoom100 = () => setScale(1)

  const displayWidth = imageSize && scale ? imageSize.w * scale : undefined
  const displayHeight = imageSize && scale ? imageSize.h * scale : undefined

  const visibleFlows = (flows ?? []).filter((f) => {
    if (filter === 'all') return true
    const known = !f.is_forked && f.upstream_point && f.downstream_point
    return filter === 'known' ? known : !known
  })

  const stats = (() => {
    if (!flows) return null
    const total = flows.length
    const bySource: Record<string, number> = {}
    let forked = 0
    let unknown = 0
    for (const f of flows) {
      if (f.is_forked) { forked++; continue }
      if (!f.upstream_point) { unknown++; continue }
      const k = f.seed_source || 'unknown'
      bySource[k] = (bySource[k] ?? 0) + 1
    }
    return { total, bySource, forked, unknown }
  })()

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-dark-card rounded-lg shadow-lg w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-4">
            <h3 className="text-base font-semibold">{t('document_viewer:flows.title', { defaultValue: 'Flow directions' })}</h3>
            {stats && (
              <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-3 flex-wrap">
                <span>total={stats.total}</span>
                <span>forked={stats.forked}</span>
                <span>unknown={stats.unknown}</span>
                {Object.entries(stats.bySource).map(([k, v]) => (
                  <span key={k}>
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1 align-middle"
                      style={{ backgroundColor: SEED_COLOR[k] ?? UNKNOWN_COLOR }}
                    />
                    {k}={v}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 border rounded">
              <button
                onClick={zoomOut}
                title={t('document_viewer:flows.zoomOut', { defaultValue: 'Zoom out' })}
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-muted"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                onClick={zoomIn}
                title={t('document_viewer:flows.zoomIn', { defaultValue: 'Zoom in' })}
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-muted"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={zoomFit}
                title={t('document_viewer:flows.fitToScreen', { defaultValue: 'Fit to screen' })}
                className="p-1 hover:bg-gray-100 dark:hover:bg-dark-muted"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button
                onClick={zoom100}
                title={t('document_viewer:flows.actualSize', { defaultValue: 'Actual size' })}
                className="px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-dark-muted"
              >
                1:1
              </button>
              {scale ? (
                <ZoomReadout
                  percent={Math.round(scale * 100)}
                  onSetPercent={handleSetZoom}
                  label={t('common:zoom.level', { defaultValue: 'Zoom level' })}
                  className="px-2 py-1 text-xs text-gray-500 tabular-nums min-w-[3.5rem] text-center hover:bg-gray-100 dark:hover:bg-dark-muted cursor-text"
                  inputClassName="px-2 py-1 text-xs tabular-nums min-w-[3.5rem] text-center bg-transparent outline-none focus:ring-2 focus:ring-blue-500 rounded"
                />
              ) : (
                <span className="px-2 py-1 text-xs text-gray-500 tabular-nums min-w-[3.5rem] text-right">—</span>
              )}
            </div>
            <label className="flex items-center gap-1 text-xs select-none cursor-pointer">
              <input
                type="checkbox"
                checked={showLabels}
                onChange={(e) => setShowLabels(e.target.checked)}
              />
              {t('document_viewer:flows.labels', { defaultValue: 'Labels' })}
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'known' | 'unknown')}
              className="text-xs border rounded px-2 py-1 bg-white dark:bg-dark-muted"
            >
              <option value="all">{t('document_viewer:flows.filterAll', { defaultValue: 'All' })}</option>
              <option value="known">{t('document_viewer:flows.filterKnown', { defaultValue: 'Known only' })}</option>
              <option value="unknown">{t('document_viewer:flows.filterUnknown', { defaultValue: 'Unknown / forked' })}</option>
            </select>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-dark-muted rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div
          ref={scrollRef}
          className="flex-1 overflow-auto relative bg-gray-50 dark:bg-dark-bg"
        >
          {error ? (
            <div className="p-8 text-sm text-red-500">{error}</div>
          ) : flows === null ? (
            <div className="p-8 text-sm text-gray-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('document_viewer:flows.loadingData', { defaultValue: 'Loading flow data…' })}
            </div>
          ) : !imageSize || !scale ? (
            <div className="p-8 text-sm text-gray-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('document_viewer:flows.loadingImage', { defaultValue: 'Loading image…' })}
            </div>
          ) : (
            <div
              className="relative"
              style={{
                width: displayWidth,
                height: displayHeight,
              }}
            >
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={t('document_viewer:flows.imageAlt', { defaultValue: 'P&ID' })}
                  className="block absolute inset-0 w-full h-full max-w-none select-none"
                />
              )}
              {imageSize && (
                <svg
                  viewBox={`0 0 ${imageSize.w} ${imageSize.h}`}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                >
                  <defs>
                    {Object.entries(SEED_COLOR).map(([k, c]) => (
                      <marker
                        key={k}
                        id={`arrow-${k}`}
                        viewBox="0 0 10 10"
                        refX="9"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                      >
                        <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
                      </marker>
                    ))}
                    <marker
                      id="arrow-unknown"
                      viewBox="0 0 10 10"
                      refX="9"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      orient="auto-start-reverse"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 z" fill={UNKNOWN_COLOR} />
                    </marker>
                  </defs>
                  {visibleFlows.map((f) => (
                    <FlowOverlay key={f.connection_id} flow={f} showLabels={showLabels} />
                  ))}
                </svg>
              )}
            </div>
          )}
        </div>
        <div className="px-3 py-2 border-t text-xs text-gray-500 dark:text-gray-400 flex gap-4 flex-wrap">
          <span className="font-medium">{t('document_viewer:flows.legend', { defaultValue: 'Legend:' })}</span>
          {Object.entries(SEED_COLOR).map(([k, c]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-0.5" style={{ backgroundColor: c }} />
              {k}
            </span>
          ))}
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-0.5" style={{ backgroundColor: UNKNOWN_COLOR, borderTop: `1px dashed ${UNKNOWN_COLOR}` }} />
            {t('document_viewer:flows.legendUnknown', { defaultValue: 'unknown' })}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-0.5" style={{ backgroundColor: FORKED_COLOR }} />
            {t('document_viewer:flows.legendForked', { defaultValue: 'forked' })}
          </span>
        </div>
      </div>
    </div>
  )
}

function FlowOverlay({ flow, showLabels }: { flow: ConnectionFlow; showLabels: boolean }) {
  if (flow.coords.length < 2) return null
  const seed = flow.seed_source ?? 'unknown'
  const isKnown = !flow.is_forked && flow.upstream_point && flow.downstream_point
  const color = flow.is_forked
    ? FORKED_COLOR
    : (isKnown ? (SEED_COLOR[seed] ?? UNKNOWN_COLOR) : UNKNOWN_COLOR)
  const markerId = isKnown ? (SEED_COLOR[seed] ? `arrow-${seed}` : 'arrow-unknown') : undefined
  const pathData = flow.coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ')

  const mid = polylineMidpoint(flow.coords)

  let arrowLine: JSX.Element | null = null
  if (isKnown && flow.upstream_point && mid) {
    const upstreamIsStart =
      Math.hypot(flow.coords[0][0] - flow.upstream_point[0], flow.coords[0][1] - flow.upstream_point[1]) <=
      Math.hypot(
        flow.coords[flow.coords.length - 1][0] - flow.upstream_point[0],
        flow.coords[flow.coords.length - 1][1] - flow.upstream_point[1],
      )
    const sign = upstreamIsStart ? 1 : -1
    const arrowLen = Math.max(12, Math.min(60, mid.total * 0.08))
    const half = arrowLen / 2
    arrowLine = (
      <line
        x1={mid.x - mid.tx * half * sign}
        y1={mid.y - mid.ty * half * sign}
        x2={mid.x + mid.tx * half * sign}
        y2={mid.y + mid.ty * half * sign}
        stroke={color}
        strokeWidth={6}
        markerEnd={markerId ? `url(#${markerId})` : undefined}
      />
    )
  }

  const label = flow.line_numbers.length > 0 ? flow.line_numbers.join(', ') : null
  let labelEl: JSX.Element | null = null
  let classLabelEl: JSX.Element | null = null
  if (showLabels && mid) {
    // Offset label perpendicular to the local tangent so it sits beside the
    // pipe instead of on top of it.
    const nx = -mid.ty
    const ny = mid.tx
    const off = 18
    const lx = mid.x + nx * off
    const ly = mid.y + ny * off
    if (label) {
      labelEl = <LineNumberLabel x={lx} y={ly} text={label} color={color} />
    }
    if (flow.note) {
      // Place the predicted-class label on the OPPOSITE side of the line so
      // it doesn't collide with the line-number label.
      const cx = mid.x - nx * off
      const cy = mid.y - ny * off
      classLabelEl = (
        <LineNumberLabel x={cx} y={cy} text={flow.note} color="#0ea5e9" />
      )
    }
  }
  // Browser-native tooltip on hover — shows class + line numbers when the
  // user mouses over the polyline, regardless of label visibility.
  const tooltipParts: string[] = []
  if (flow.note) tooltipParts.push(`class: ${flow.note}`)
  if (label) tooltipParts.push(`lines: ${label}`)
  const tooltip = tooltipParts.join(' · ') || null

  return (
    <g>
      {tooltip ? <title>{tooltip}</title> : null}
      <path
        d={pathData}
        fill="none"
        stroke={color}
        strokeWidth={3}
        strokeDasharray={isKnown ? undefined : '6 4'}
        opacity={0.85}
      />
      {arrowLine}
      {isKnown && flow.upstream_point && (
        <circle cx={flow.upstream_point[0]} cy={flow.upstream_point[1]} r={5} fill={color} />
      )}
      {isKnown && flow.downstream_point && (
        <rect
          x={flow.downstream_point[0] - 5}
          y={flow.downstream_point[1] - 5}
          width={10}
          height={10}
          fill={color}
        />
      )}
      {labelEl}
      {classLabelEl}
    </g>
  )
}

function LineNumberLabel({
  x, y, text, color,
}: { x: number; y: number; text: string; color: string }) {
  // Approximate text box sizing so the white backdrop covers the glyphs.
  // Actual widths vary per glyph, but an estimate is fine here.
  const fontSize = 18
  const charW = fontSize * 0.55
  const padX = 6
  const padY = 3
  const w = text.length * charW + padX * 2
  const h = fontSize + padY * 2
  return (
    <g>
      <rect
        x={x - w / 2}
        y={y - h / 2}
        width={w}
        height={h}
        rx={3}
        ry={3}
        fill="white"
        fillOpacity={0.92}
        stroke={color}
        strokeWidth={1}
      />
      <text
        x={x}
        y={y}
        fontFamily="ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
        fontSize={fontSize}
        fontWeight={600}
        fill="#111827"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {text}
      </text>
    </g>
  )
}

// Compute the arc-length midpoint of a polyline and its local unit tangent
// (pointing from coords[0] toward coords[last]). Returns null for degenerate
// geometries.
function polylineMidpoint(
  coords: Array<[number, number]>,
): { x: number; y: number; tx: number; ty: number; total: number } | null {
  if (coords.length < 2) return null
  const segs: Array<{ len: number; cum: number }> = []
  let total = 0
  for (let i = 0; i < coords.length - 1; i++) {
    const [ax, ay] = coords[i]
    const [bx, by] = coords[i + 1]
    const len = Math.hypot(bx - ax, by - ay)
    total += len
    segs.push({ len, cum: total })
  }
  if (total === 0) return null

  const target = total / 2
  let segIdx = 0
  let into = 0
  for (let i = 0; i < segs.length; i++) {
    if (segs[i].cum >= target) {
      segIdx = i
      into = target - (i === 0 ? 0 : segs[i - 1].cum)
      break
    }
  }
  const [ax, ay] = coords[segIdx]
  const [bx, by] = coords[segIdx + 1]
  const segLen = segs[segIdx].len
  if (segLen === 0) return null
  const t = into / segLen
  const x = ax + (bx - ax) * t
  const y = ay + (by - ay) * t

  const dx = bx - ax
  const dy = by - ay
  const mag = Math.hypot(dx, dy)
  if (mag === 0) return null
  return { x, y, tx: dx / mag, ty: dy / mag, total }
}
